import { Component, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { cloneDeep, filter, findIndex } from 'lodash';
import { EMPTY as empty, Observable, Subject, Subscription, SubscriptionLike as ISubscription } from 'rxjs';
import { skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { LIST_TYPE } from '../../../../shared/constants/list-view/list-view.constant';
import { Toggles } from '../../../../shared/constants/toggles.constant';
import { DateHelpers } from '../../../../shared/services/date-helpers/date-helpers.service';
import { ListSummaryGroupings } from '../../../../shared/services/list-summary-groupings/list-summary-groupings.service';
import { AssessmentsGrouping } from '../../../../shared/services/mad-lib-services/grouping-functions/assessments/assessments-groupings';
import { ToggleService } from '../../../../shared/services/toggle/toggle.service';
import { ISchoolAssessment } from '../../../../shared/typings/interfaces/school-assessment.interface';
import { ToggleBatchActions, UpdateSelectedStudentIds } from '../../../../store';
import { IAcademicListStudent } from '../../../../store/effects/projections';
import { ListSearchComponent } from '../../list-search/list-search.component';
import { CoursePriorityGrouping } from './../../../../shared/services/mad-lib-services/grouping-functions/course-priority/course-priority';
import { MadLibGroupings } from './../../../../shared/services/mad-lib-services/mad-lib-groupings';
import { IMadlibOptions, MadLibHelpers } from './../../../../shared/services/mad-lib-services/mad-lib-helpers';
import { MadLibSorting } from './../../../../shared/services/mad-lib-services/mad-lib-sorting';
import { IListViewData } from './../../../../shared/typings/interfaces/list-view.interface';
import { DIMENSION_1, DIMENSION_2, GROUPING, ORIGINAL_DIMENSION_2 } from './academic-mad-lib.constant';
import {
  IAcademicDimension,
  IAcademicDimensionCol,
  IAcademicDimensionOp,
  IAcademicMadLibModel,
  IConstants,
  TAcademicDimension,
} from './academic-mad-lib.interface';

/* istanbul ignore next: Ignoring this while changes to madlib options are in progress  */
@Component({
  selector: 'academic-mad-lib',
  template: require('./academic-mad-lib.component.html'),
})
export class AcademicMadLib implements OnInit, OnDestroy {
  dimension1;
  dimension2;
  groupings;
  // all dims array each w/ broad set of options
  dimensions;
  // all dimensions refined based on user and schoolType
  filteredAcademicMadLib;
  // an object containing the currently selected values for each dimension
  madLibModel;
  // dim1 options generated specifically for the user
  madlibOptions: IMadlibOptions;
  selectedDimension1;
  selectedDimension2;
  // aggregate of props we need to pass often { }
  defaultSortData;
  filteredStudents$: Observable<IAcademicListStudent[]>;
  groupings$;
  sortedGroupings$;
  schoolType: string;
  columns;
  sortState = { sortedColumn: null, sortOrder: null, dataType: null };
  destroy$: Subject<boolean> = new Subject<boolean>();
  listType = LIST_TYPE.ACADEMIC;
  batchEditMode: boolean;
  searchQuery: string;
  prevSearch: string;
  madlibStateSubscription: ISubscription;
  setPanelShadowStyle: boolean = false;
  fAndPAdminNumber: number = 0;
  constantsWithAdmin: IConstants;
  schoolAssessments: ISchoolAssessment[];
  schoolAssessmentSubscription: Subscription;

  @Input() currentSchool;
  @Input() currentUser;
  @Input() madlibOptions$;
  @Input() studentsWithFlagsAndSupports$;
  @Input() schoolAssessments$: Observable<ISchoolAssessment[]>;
  @Input() madlibState$;
  @Input() batchActionsMode$: Observable<any>;
  @Input() constants: IConstants;

  @ViewChild(ListSearchComponent) searchComponent: ListSearchComponent;

  constructor(
    private coursePriorityGrouping: CoursePriorityGrouping,
    private assessmentsGrouping: AssessmentsGrouping,
    private madLibGroupings: MadLibGroupings,
    private madLibHelpers: MadLibHelpers,
    private madLibSorting: MadLibSorting,
    private store: Store<any>,
    private listSummaryGroupings: ListSummaryGroupings,
    private toggleService: ToggleService,
    private dateHelpers: DateHelpers,
    @Inject('$location') public $location: any,
  ) {}

  ngOnInit(): void {
    const nyctToggle = this.toggleService.getToggleState(Toggles.TOGGLE_NYCT_MVP);
    const schoolType = this.currentSchool.schoolType;
    this.dimension1 = DIMENSION_1;
    this.dimension2 = cloneDeep(ORIGINAL_DIMENSION_2);
    DIMENSION_2.options = cloneDeep(ORIGINAL_DIMENSION_2.options);

    // TODO: remove this filter once the NYCT toggle is on for all users (JE)
    /* istabul ignore next */
    if (!nyctToggle) {
      const dim2 = Object.assign({}, DIMENSION_2);
      dim2.options.ES = filter(dim2.options.ES, option => option.key !== 'F_&_P_ELA');
      dim2.options.MS = filter(dim2.options.MS, option => option.key !== 'F_&_P_ELA');
      this.dimension2 = dim2;
    }
    this.schoolAssessmentSubscription = this.schoolAssessments$.subscribe(schoolAssessments => {
      this.schoolAssessments = schoolAssessments;
      // identify which admin is the most recently completed
      const today = this.dateHelpers.moment();
      const filteredAssessments = filter(schoolAssessments, assmnt => {
        const endMoment = this.dateHelpers.moment(assmnt.startDate);
        return today > endMoment;
      });
      this.fAndPAdminNumber = filteredAssessments.length ? filteredAssessments.reverse()[0].adminNumber : 1;
      const constantsWithAdmin = Object.assign({}, this.constants);
      constantsWithAdmin.adminNumber = this.fAndPAdminNumber;
      this.constantsWithAdmin = constantsWithAdmin;
    });

    this.groupings = GROUPING;
    this.dimensions = [this.dimension1, this.dimension2, this.groupings];
    this.defaultSortData = { sortKey: 'studentName', orderBy: 'asc', dataType: 'String' };
    this.selectedDimension1 = this.dimension1.options[schoolType][0];
    this.selectedDimension2 = this.dimension2.options[this.selectedDimension1.schoolLevel];

    // if students or any of the merged data changes, re-run the ui logic
    this.studentsWithFlagsAndSupports$
      .pipe(
        takeUntil(this.destroy$),
        skip(1),
      )
      .subscribe(() => this.updateGroupings());

    this.initializeInitialLoad();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  // todo: dry this (dvn)
  search(searchTerm) {
    const formattedSearchTerm = this.madLibHelpers.formatListSearchTerm(searchTerm);
    this.searchQuery = formattedSearchTerm;

    this.clearBatchEditsInProgress();

    let searchStudents$ = this.madLibHelpers.getFilteredStudents(
      this.studentsWithFlagsAndSupports$,
      this.madLibModel.Dimension1,
    );

    searchStudents$ = this.madLibHelpers.applySearchFilter(searchStudents$, formattedSearchTerm);
    this.groupings$ = this.getStudentGrouping(searchStudents$, this.madLibModel, this.columns);
    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(null, null, validColumns);

    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      true,
      null,
      this.madLibModel,
    );
    this.madLibHelpers.updateUrl(this.madLibModel, {
      sortKey,
      dataType,
      orderBy,
      search: this.searchQuery,
      list: LIST_TYPE.ACADEMIC,
    });
  }

  clearSearch(): void {
    this.searchComponent.clearSearch();
  }

  // run on init & sort updates
  sort(newSort?): void {
    this.clearBatchEditsInProgress();
    // confirm that only a valid column can be sorted on
    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(newSort, false, validColumns);

    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      true, // this is set to true since this run function is only called on a new sort
      null,
      this.madLibModel,
    );

    this.madLibHelpers.updateUrl(this.madLibModel, { sortKey, dataType, orderBy, search: this.searchQuery });
    this.sortState = this.madLibSorting.updateSortState(sortKey, orderBy, dataType);
  }

  getValidColumns(columns): string[] {
    return columns.map(({ key }) => key);
  }

  // wrapper for run mode side-effects
  initializeInitialLoad(): void {
    this.madlibStateSubscription = this.madlibState$.pipe(take(1)).subscribe(madlibState => {
      this.filteredAcademicMadLib = madlibState.filteredMadLib;
      this.madLibModel = madlibState.madLibModel;
    });
    this.columns = this.madLibHelpers.getListColumns(this.madLibModel, this.constantsWithAdmin);
    this.filteredStudents$ = this.madLibHelpers.getFilteredStudents(
      this.studentsWithFlagsAndSupports$,
      this.madLibModel.Dimension1,
    );
    this.groupings$ = this.getStudentGrouping(this.filteredStudents$, this.madLibModel, this.columns);
    // confirm that only a valid column can be sorted on
    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(null, false, validColumns);

    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      true, // this is set to true since this run function is only called on a new sort
      null,
      this.madLibModel,
    );
    this.madLibHelpers.updateUrl(this.madLibModel, { sortKey, dataType, orderBy, search: this.searchQuery });
    this.sortState = this.madLibSorting.updateSortState(sortKey, orderBy, dataType);
    const urlValues = this.$location.search();
    this.prevSearch = this.madLibHelpers.getSearchTerm(urlValues, LIST_TYPE.ACADEMIC);
    this.selectedDimension1 = this.madLibModel.Dimension1;
  }

  updateGroupings() {
    this.batchActionsMode$
      .pipe(
        switchMap(mode => {
          return mode ? empty : this.madlibState$;
        }),
        tap((madlibState: any) => {
          if (madlibState) {
            this.filteredAcademicMadLib = madlibState.filteredMadLib;
            this.madLibModel = this.madLibModel;
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.filteredStudents$ = this.madLibHelpers.getFilteredStudents(
      this.studentsWithFlagsAndSupports$,
      this.madLibModel.Dimension1,
    );
    this.filteredStudents$ = this.madLibHelpers.applySearchFilter(this.filteredStudents$, this.searchQuery);
    this.groupings$ = this.getStudentGrouping(this.filteredStudents$, this.madLibModel, this.columns);
    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(true, true, validColumns);

    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      false,
      null,
      this.madLibModel,
    );
  }

  // fired when droodown options are changed
  update(
    madLibDimension: IAcademicDimension,
    subOption: IAcademicDimensionOp,
    dimensions: IAcademicDimension[],
    currentMadLibModel: IAcademicMadLibModel,
    currentMadLib: IAcademicDimension[], // all the options before the interdependencies
    defaultSortData,
  ): void {
    this.clearBatchEditsInProgress();
    const { sortKey, orderBy, dataType } = defaultSortData;
    const { madLib: dimension } = madLibDimension;

    this.sortState = { sortedColumn: null, sortOrder: null, dataType: null };

    switch (dimension) {
      case 'Dimension1': {
        // update selected options -- keep track to highlight current selection once madlib options open
        this.selectedDimension1 = subOption;

        // confirm model is still valid after dimemsions change -- check dim interdependency
        this.madLibModel = this.validateAndUpdateMadlibModel(dimensions, subOption, dimension, currentMadLibModel);

        // bc of the interdependecies between dimensions, we need to update the pill options on change as well
        this.filteredAcademicMadLib = this.updateMadLibOptionsOnDim1Change(subOption, dimensions);

        this.madLibHelpers.updateUrl(this.madLibModel, defaultSortData);
        //  only on dimension1 change we need to refilter the students
        this.filteredStudents$ = this.madLibHelpers.getFilteredStudents(
          this.studentsWithFlagsAndSupports$,
          this.selectedDimension1,
        );

        this.filteredStudents$ = this.madLibHelpers.applySearchFilter(this.filteredStudents$, this.searchQuery);
        this.columns = this.madLibHelpers.getListColumns(this.madLibModel, this.constantsWithAdmin);
        this.groupings$ = this.getStudentGrouping(this.filteredStudents$, this.madLibModel, this.columns);
        this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
          this.groupings$,
          sortKey,
          dataType,
          orderBy,
          this.sortState,
          false,
          null,
          this.madLibModel,
        );
        break;
      }

      case 'Dimension2': {
        this.selectedDimension2 = subOption;

        this.madLibModel = this.validateAndUpdateMadlibModel(dimensions, subOption, dimension, currentMadLibModel);

        // bc of the interdependecies between dimensions, we need to update the pill options on change
        this.filteredAcademicMadLib = this.updateMadLibOptionsOnDim2Change(subOption, currentMadLib);

        this.madLibHelpers.updateUrl(this.madLibModel, defaultSortData);
        this.columns = this.madLibHelpers.getListColumns(this.madLibModel, this.constantsWithAdmin);

        this.filteredStudents$ = this.madLibHelpers.applySearchFilter(this.filteredStudents$, this.searchQuery);
        this.groupings$ = this.getStudentGrouping(this.filteredStudents$, this.madLibModel, this.columns);
        this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
          this.groupings$,
          sortKey,
          dataType,
          orderBy,
          this.sortState,
          false,
          null,
          this.madLibModel,
        );
        break;
      }

      case 'Groupings': {
        this.madLibModel = this.madLibHelpers.updateMadLibModel(currentMadLibModel, dimension, subOption);
        this.madLibHelpers.updateUrl(this.madLibModel, defaultSortData);
        this.columns = this.madLibHelpers.getListColumns(this.madLibModel, this.constantsWithAdmin);
        this.groupings$ = this.getStudentGrouping(this.filteredStudents$, this.madLibModel, this.columns);
        this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
          this.groupings$,
          sortKey,
          dataType,
          orderBy,
          this.sortState,
          false,
          null,
          this.madLibModel,
        );
        break;
      }
      default:
        return;
    }
  }

  /**
   * Why this complexity?
   * Within this madlib there is a tight coupling between the madlib selections
   * When a user selects a `new` dimension1 there is no guarantee the current dimension2 is still a valid option
   * When a user selects a `new` dimension2 there is no guarantee the current grouping is still a valid option
   * This function valids the model based on the new selection and if valid, returns the same madlib model
   * If it is invalid, it will return a new valid madlib model based on the new selection (JJ)
   */
  validateMadlibModel(
    madLib: IAcademicDimension[],
    groupings: IAcademicDimension,
    newSelection,
    madLibModel: IAcademicMadLibModel,
  ) {
    const modifiedDimension = newSelection.dimension;
    const newSelectionKey = newSelection.key;
    switch (modifiedDimension) {
      case 1: {
        const dim2IsValid = this.confirmDim2SelectionValidity(madLibModel, newSelectionKey, madLib[1]);
        if (!dim2IsValid) {
          madLibModel = this.defaultSingleDimensionModel(1, madLib, madLibModel);
          const updatedDimension2Selection = madLibModel.Dimension2.key;
          const groupingIsValid = this.confirmGroupingSelectionValidity(
            madLibModel,
            updatedDimension2Selection,
            groupings,
          );
          if (!groupingIsValid) madLibModel = this.defaultSingleDimensionModel(2, madLib, madLibModel);
          return madLibModel;
        } else return madLibModel;
      }
      case 2: {
        const groupingIsValid = this.confirmGroupingSelectionValidity(madLibModel, newSelectionKey, groupings);
        if (!groupingIsValid) madLibModel = this.defaultSingleDimensionModel(2, madLib, madLibModel);
        return madLibModel;
      }
      default:
        return madLibModel;
    }
  }

  // need to pass in this.dimensions
  validateAndUpdateMadlibModel(
    dimensions: IAcademicDimension[],
    option: IAcademicDimensionOp,
    dimension: TAcademicDimension,
    madlibModel: IAcademicMadLibModel,
  ) {
    const [, , groupings] = dimensions;
    let updatedMadlib =
      dimension === 'Dimension1'
        ? this.updateMadLibOptionsOnDim1Change(option, dimensions)
        : this.updateMadLibOptionsOnDim2Change(option, dimensions);

    const validatedMadlibModel = this.validateMadlibModel(updatedMadlib, groupings, option, madlibModel);
    return this.madLibHelpers.updateMadLibModel(validatedMadlibModel, dimension, option);
  }

  getStudentGrouping(
    filteredStudents$,
    madLibModels,
    columns: IAcademicDimensionCol,
  ): Observable<Partial<IListViewData>> {
    const {
      Groupings: { key: groupingsKey },
    } = madLibModels;
    let groupings$: Observable<Partial<IListViewData>>;
    switch (groupingsKey) {
      case 'DOE_RISK_GROUP':
        groupings$ = this.madLibGroupings.getDoeRiskGroupings(
          filteredStudents$,
          madLibModels,
          columns,
          this.listType,
          this.currentSchool.isIreadySchool,
        );
        break;
      case 'SCHOOL_RISK_GROUP':
        groupings$ = this.madLibGroupings.getSchoolRiskGroupings(
          filteredStudents$,
          madLibModels,
          columns,
          this.listType,
          this.currentSchool.isIreadySchool,
        );
        break;
      case 'PROGRESS':
        groupings$ = this.madLibGroupings.getProgressGroupings(filteredStudents$, madLibModels, columns, this.listType);
        break;
      case 'SUPPORT':
        groupings$ = this.madLibGroupings.getSupportGroupings(filteredStudents$, columns, this.listType, madLibModels);
        break;
      case 'SUBJECT_SUPPORT':
        groupings$ = this.madLibGroupings.getSubjectSupportGroupings(
          filteredStudents$,
          columns,
          this.listType,
          madLibModels,
        );
        break;
      case 'FLAG':
        groupings$ = this.madLibGroupings.getFlagGroupings(filteredStudents$, columns, this.listType, madLibModels);
        break;
      case 'PRIORITY':
        groupings$ = this.coursePriorityGrouping.getGrouping(filteredStudents$, {
          columns,
          madLibModels,
          listType: this.listType,
        });
        break;
      case 'F_&_P_ELA_REL_SCORE':
        groupings$ = this.assessmentsGrouping.getFandPRelativeScoreGrouping(filteredStudents$, {
          columns,
          listType: this.listType,
          admin: this.fAndPAdminNumber,
        });
        break;
      case 'F_&_P_ELA_INSTRUC_SCORE':
        groupings$ = this.assessmentsGrouping.getFandPInstScoreGrouping(filteredStudents$, {
          columns,
          listType: this.listType,
          admin: this.fAndPAdminNumber,
        });
        break;
      case 'F_&_P_ELA_SUPPORT':
        groupings$ = this.assessmentsGrouping.getFandPSupportGrouping(filteredStudents$, {
          columns,
          listType: this.listType,
          admin: this.fAndPAdminNumber,
        });
        break;
      default:
        groupings$ = null;
        break;
    }
    this.listSummaryGroupings.setGroupings(madLibModels, groupings$);
    return groupings$;
  }

  // see note above on the need for this function
  confirmDim2SelectionValidity(
    madLibModel: IAcademicMadLibModel,
    dimension1Selection: string,
    dimension2: IAcademicDimension,
  ): boolean {
    const currentDim2Selection = madLibModel.Dimension2.key;
    const dim2OptionsBasedOnNewSelection = dimension2.options[dimension1Selection];
    const selectionIsValid = findIndex(dim2OptionsBasedOnNewSelection, { key: currentDim2Selection }) !== -1;
    return selectionIsValid;
  }

  // see note above on the need for this function
  confirmGroupingSelectionValidity(
    madLibModel: IAcademicMadLibModel,
    dimension2Selection: string,
    groupings: IAcademicDimension,
  ): boolean {
    const currentGroupingsSelection = madLibModel.Groupings.key;
    const groupingsOptionsBasedOnNewSelection = groupings.options[dimension2Selection];
    const selectionIsValid =
      findIndex(groupingsOptionsBasedOnNewSelection, (option: IAcademicDimensionOp) => {
        // validString is currently only used to distinguish ela/math in doe/school risk group options
        // and is added to interface IAcademicDimensionOp
        // could be easily modified to fit other needs down the road (JChu)
        return madLibModel.Groupings.validString
          ? option.validString === madLibModel.Groupings.validString
          : option.key === currentGroupingsSelection;
      }) !== -1;
    return selectionIsValid;
  }

  // default single returning to the first of the dimension options
  defaultSingleDimensionModel(
    index: number,
    madLib: IAcademicDimension[],
    madLibModel: IAcademicMadLibModel,
  ): IAcademicMadLibModel {
    const model = madLib.reduce((model, { madLib, options }, i) => {
      if (index === i) model[madLib] = options[0];
      return model;
    }, {});
    return { ...madLibModel, ...model };
  }

  updateMadLibOptionsOnDim1Change(
    option: IAcademicDimensionOp,
    currentMadLib: IAcademicDimension[],
  ): IAcademicDimension[] {
    const { schoolLevel } = option;
    const [, dimension2, groupings] = currentMadLib;
    const dimension2Options = dimension2.options[schoolLevel];
    const dimension2DefaultValue = dimension2Options[0].key;
    const groupingsOptions = groupings.options[dimension2DefaultValue];
    return [
      // todo: pass this in to keep this function pure
      this.filteredAcademicMadLib[0],
      { ...dimension2, ...{ options: dimension2Options } },
      { ...groupings, ...{ options: groupingsOptions } },
    ];
  }

  updateMadLibOptionsOnDim2Change(
    option: IAcademicDimensionOp,
    currentMadLib: IAcademicDimension[],
  ): IAcademicDimension[] {
    // dim2 selection impacts groupings dropdown options
    const dimension2Selection = option.key;
    const groupings = this.groupings.options[dimension2Selection];
    return [currentMadLib[0], currentMadLib[1], { ...this.groupings, ...{ options: groupings } }];
  }

  // TODO: update how this functions and update html as well
  isOptionActive(lib: IAcademicDimension, option: string): boolean {
    if (lib.madLib === 'Dimension1') return this.selectedDimension1.key === option;
    return this.selectedDimension2.key === option;
  }

  // candidate to move to helper
  clearBatchEditsInProgress(): void {
    this.batchEditMode = false;
    this.store.dispatch(new ToggleBatchActions(this.batchEditMode));
    this.store.dispatch(new UpdateSelectedStudentIds([]));
  }
}
